import { Spinner } from '@/components/ui/spinner';
import type { RunSummary } from '@/entities/sender.entity';
import SenderRunListItem from '@/pages/sender/sender-run-list-item';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef, type RefObject } from 'react';

export const SENDER_RUN_CARD_ESTIMATE_PX = 106;
const LIST_GAP_PX = 8;
const LOAD_MORE_NEAR_END = 5;

interface SenderRunsVirtualFeedProps {
	runs: RunSummary[];
	hasMore: boolean;
	isFetchingMore: boolean;
	loadMoreError: boolean;
	scrollRef: RefObject<HTMLDivElement | null>;
	onLoadMore: () => void;
	onRunClick: (run: RunSummary) => void;
}

export default function SenderRunsVirtualFeed({
	runs,
	hasMore,
	isFetchingMore,
	loadMoreError,
	scrollRef,
	onLoadMore,
	onRunClick,
}: SenderRunsVirtualFeedProps) {
	const loadMoreGateRef = useRef(-1);
	const prevRunsLengthRef = useRef(0);

	const virtualizer = useVirtualizer({
		count: runs.length,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => SENDER_RUN_CARD_ESTIMATE_PX,
		overscan: 6,
		gap: LIST_GAP_PX,
		measureElement: el => el.getBoundingClientRect().height,
		enabled: runs.length > 0,
	});

	const virtualItems = virtualizer.getVirtualItems();
	const lastVisibleIndex = virtualItems.at(-1)?.index ?? -1;

	useEffect(() => {
		if (runs.length > prevRunsLengthRef.current) {
			loadMoreGateRef.current = -1;
		}
		prevRunsLengthRef.current = runs.length;
	}, [runs.length]);

	useEffect(() => {
		if (!hasMore || isFetchingMore || runs.length === 0) return;
		const threshold = runs.length - LOAD_MORE_NEAR_END;
		if (lastVisibleIndex < threshold) return;
		if (loadMoreGateRef.current >= threshold) return;
		loadMoreGateRef.current = lastVisibleIndex;
		onLoadMore();
	}, [lastVisibleIndex, runs.length, hasMore, isFetchingMore, onLoadMore]);

	if (runs.length === 0) {
		return <p className='text-[13px] text-muted-foreground'>Запусков пока нет</p>;
	}

	return (
		<div className='flex flex-col gap-2'>
			<p className='text-[13px] text-muted-foreground'>Запуски</p>
			<div
				style={{
					height: `${virtualizer.getTotalSize()}px`,
					width: '100%',
					position: 'relative',
				}}
			>
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
					}}
				>
					{virtualItems.map(({ index, key }) => {
						const run = runs[index];
						if (!run) return null;
						return (
							<div key={key} data-index={index} ref={virtualizer.measureElement} className='pb-2'>
								<SenderRunListItem run={run} onClick={() => onRunClick(run)} />
							</div>
						);
					})}
				</div>
			</div>
			{isFetchingMore && hasMore ? (
				<div className='flex justify-center py-2'>
					<Spinner />
				</div>
			) : null}
			{loadMoreError ? (
				<p className='text-center text-[13px] text-muted-foreground'>Не удалось подгрузить ещё</p>
			) : null}
		</div>
	);
}

export function measurePrependedRunsHeight(
	anchor: HTMLElement | null,
	prependedCount: number,
	fallbackRowPx = SENDER_RUN_CARD_ESTIMATE_PX,
): number {
	if (prependedCount <= 0) return 0;
	let height = 0;
	for (let i = 0; i < prependedCount; i++) {
		const el = anchor?.querySelector(`[data-index="${i}"]`) as HTMLElement | null;
		height += (el?.getBoundingClientRect().height ?? fallbackRowPx) + LIST_GAP_PX;
	}
	return height;
}
